import { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import {
    LocalizationProvider,
    DatePicker,
    TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";
import { format } from "date-fns";
import HelperFunctions from "../../shared utils/Utils";
import { SERVER_URL } from "../../../constants.js";


function EditTransfer(props) {
    const {
      person_id,
      transfer_id,
      transferDate,
      editTransfer,
      open: openProp,
      onClose: onCloseProp,
      hideTrigger = false,
      onSaved,
      isAdult = false,
    } = props;

    const [openInternal, setOpenInternal] = useState(false);

    const isControlled = typeof openProp === "boolean";
    const open = isControlled ? openProp : openInternal;

    const [airports, setAirports] = useState([]);
    const [stays, setStays] = useState([]);

    // Load the person's stays so we can sanity-check the flight date against the
    // start/end of their stay at save time. Adults use NSRs (different endpoint
    // and lowercase field names) — checking an adult against api/student/{id}
    // pulled a *different* student with the same id and produced a nonsense
    // warning.
    useEffect(() => {
        if (!open || !person_id) return;
        const token = sessionStorage.getItem("bearer");
        const url = isAdult
            ? `${SERVER_URL}api/nsrs/${person_id}`
            : `${SERVER_URL}api/student/${person_id}/stays`;
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => (r.ok ? r.json() : []))
            .then((data) => {
                const list = Array.isArray(data) ? data : [];
                setStays(
                    isAdult
                        ? list.map((n) => ({
                              arrivalDate: n.arrivaldate,
                              departureDate: n.departuredate,
                          }))
                        : list
                );
            })
            .catch(() => setStays([]));
    }, [open, person_id, isAdult]);

    const isServedAirportCode = (val) => {
        const v = String(val || "").trim().toUpperCase();
        if (!v) return false;
            return airports.some((a) => String(a.code || "").toUpperCase() === v);
    };

    useEffect(() => {
        const loadAirports = async () => {
            try {
                const token = sessionStorage.getItem("bearer");
                const res = await fetch(`${SERVER_URL}api/airports`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`airports HTTP ${res.status}`);
                const data = await res.json();
                setAirports(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("Failed to load airports", e);
                setAirports([]);
            }
        };
        loadAirports();
    }, []);

    const currentHour = new Date();
    currentHour.setMinutes(0, 0, 0);

    const [transfer, setTransfer] = useState({
        direction: "IN",
        transferDate: transferDate || currentHour,
        depart: "",
        arrive: "",
        isAirportTransfer: true,
        privatePickup: false,
        unaccompaniedMinor: false,
        departureTime: currentHour,
        arrivalTime: currentHour,
        flightId: "",
        notes: "",
        internalTransfer: false,
    });

    useEffect(() => {
        const fetchTransfer = async () => {
            
            try {
                const token = sessionStorage.getItem("bearer");
                const response = await fetch(`${SERVER_URL}api/transferid/${transfer_id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const selectedTransfer = await response.json();

                if (selectedTransfer) {
                    const dir = selectedTransfer.direction || "IN";
                    const dep = selectedTransfer.depart || "";
                    const arr = selectedTransfer.arrive || "";

                    const inferredAirport = airports.length > 0
                        ? (dir === "IN" ? isServedAirportCode(arr) : isServedAirportCode(dep))
                        : true;

                    setTransfer({
                        direction: dir,
                        transferDate: selectedTransfer.transferDate
                            ? new Date(selectedTransfer.transferDate)
                            : currentHour,
                        depart: dep,
                        arrive: arr,

                        // 🔥 NEW FIELD
                        isAirportTransfer: inferredAirport,

                        privatePickup: selectedTransfer.privatePickup || false,
                        unaccompaniedMinor: Boolean(selectedTransfer.unaccompaniedMinor),
                        departureTime: selectedTransfer.departureTime
                        ? new Date(`1970-01-01T${selectedTransfer.departureTime}`)
                        : currentHour,
                        arrivalTime: selectedTransfer.arrivalTime
                        ? new Date(`1970-01-01T${selectedTransfer.arrivalTime}`)
                        : currentHour,
                        flightId: selectedTransfer.flightId || "",
                        notes: selectedTransfer.notes || "",
                        internalTransfer: selectedTransfer.internalTransfer || false,
                    });
                }
            } catch (error) {
                console.error("Error fetching transfer:", error);
            }
        };

        if (open && transfer_id) {
            fetchTransfer();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, transfer_id]);

    useEffect(() => {
        if (airports.length > 0) {
            setTransfer(prev => {
                const inferred = prev.direction === "IN"
                    ? isServedAirportCode(prev.arrive)
                    : isServedAirportCode(prev.depart);
                if (prev.isAirportTransfer === inferred) return prev;
                return { ...prev, isAirportTransfer: inferred };
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [airports]);

    const handleClickOpen = () => {
        if (!isControlled) setOpenInternal(true);
    };

    const handleClose = () => {
        onCloseProp?.();
        if (!isControlled) setOpenInternal(false);
    };

    const handleDateChange = (date, name) => {
        setTransfer((prev) => ({ ...prev, [name]: date }));
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;

        if (type === "checkbox") {
            setTransfer((prev) => {
                const updated = { ...prev, [name]: checked };

                if (name === "privatePickup") {
                    updated.flightId = checked ? "SELFTRANSFER" : "";
                }

                return updated;
            });
        } else {
            setTransfer((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = async () => {
        const requiresFlight =
        !transfer.internalTransfer && !!transfer.isAirportTransfer && !transfer.privatePickup;

        if (requiresFlight) {
            const f = (transfer.flightId || "").trim().toUpperCase();
            if (!f) {
                alert("Flight Number is required for airport transfers (or set to PENDING).");
                return;
            }
            if (f !== "PENDING") {
                const valid = HelperFunctions.validateFlight(
                    transfer,
                    setTransfer,
                    /^([A-Z]{3}|[A-Z][A-Z0-9]|[0-9][A-Z])\d{1,4}$/
                );
                if (!valid) return;
            }
        }

        // Warn (don't block) when an airport transfer's date isn't on the
        // student's stay start/end — the usual cause of a student vanishing from
        // transfer lists. Internal (campus-to-campus) transfers are exempt.
        if (!transfer.internalTransfer) {
            const dateCheck = HelperFunctions.checkTransferDateAgainstStays(transfer, stays);
            if (!dateCheck.onBoundary) {
                const word = dateCheck.direction === "OUT" ? "departure" : "arrival";
                const expectedStr = dateCheck.expected.length
                    ? dateCheck.expected.join(" or ")
                    : "—";
                const proceed = window.confirm(
                    `This ${word} flight is dated ${dateCheck.target}, but the ${word} ` +
                    `date of the stay is ${expectedStr}.\n\nTransfers dated away from the start/end of the stay ` +
                    `usually don't show on the right transfer list. Save anyway?`
                );
                if (!proceed) return;
            }
        }

        const effectiveFlightId = transfer.privatePickup
        ? "SELFTRANSFER"
        : (transfer.flightId?.trim() || "");

        const normalised = { ...transfer };
        delete normalised.isAirportTransfer;

        if (!transfer.internalTransfer) {
            const isAirport = !!transfer.isAirportTransfer;
            const dir = transfer.direction;

            if (isAirport) {
                if (dir === "IN") {
                    normalised.arrive = transfer.arrive || null;
                    normalised.depart = transfer.depart || null;
                } else {
                    normalised.depart = transfer.depart || null;
                    normalised.arrive = transfer.arrive || null;
                }
            } else {
                if (dir === "IN") {
                    normalised.depart = null;
                    normalised.arrive = transfer.arrive || null;
                } else {
                    normalised.arrive = null;
                    normalised.depart = transfer.depart || null;
                }
            }
        }

        const formattedData = {
            ...normalised,
            flightId: effectiveFlightId ? effectiveFlightId : null,
            transferDate: normalised.transferDate ? format(normalised.transferDate, "yyyy-MM-dd") : null,
            departureTime: normalised.departureTime ? format(normalised.departureTime, "HH:mm:ss") : null,
            arrivalTime: normalised.arrivalTime ? format(normalised.arrivalTime, "HH:mm:ss") : null,
            internalTransfer: Boolean(normalised.internalTransfer),
        };

        await Promise.resolve(editTransfer(formattedData, transfer_id));

        if (formattedData.flightId && formattedData.flightId !== "SELFTRANSFER") {
            try {
                const token = sessionStorage.getItem("bearer");
                const flightDate = formattedData.transferDate;
                await fetch(
                    `${SERVER_URL}api/flights/singleFlight` +
                    `?flightID=${encodeURIComponent(formattedData.flightId)}` +
                    `&flightDate=${encodeURIComponent(flightDate)}`,
                    {
                        method: "GET",
                        headers: {
                            Accept: "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            } catch (err) {
                console.error("Failed to upsert flight:", err);
            }
        }

        await Promise.resolve(onSaved?.(formattedData, person_id, transfer_id));
        handleClose();
    };


    return (
        <div>
            {!hideTrigger && (
                <IconButton onClick={handleClickOpen}>
                    <EditIcon color="primary" />
                </IconButton>
            )}
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Edit Transfer</DialogTitle>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                        <Stack spacing={2} mt={1}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="internalTransfer"
                                        checked={transfer.internalTransfer}
                                        onChange={handleChange}
                                    />
                                }
                                label="Internal Transfer (campus to campus)"
                            />
                            <FormControl>
                                <InputLabel id="direction-label">
                                    Direction
                                </InputLabel>
                                <Select
                                    labelId="direction-label"
                                    id="direction"
                                    name="direction"
                                    value={transfer.direction}
                                    onChange={handleChange}
                                >
                                    <MenuItem value="IN">IN</MenuItem>
                                    <MenuItem value="OUT">OUT</MenuItem>
                                </Select>
                            </FormControl>

                            {!transfer.internalTransfer && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="isAirportTransfer"
                                            checked={!!transfer.isAirportTransfer}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Airport transfer"
                                />
                            )}

                            <DatePicker
                                label="Date"
                                value={transfer.transferDate || new Date()}
                                onChange={(date) =>
                                    handleDateChange(date, "transferDate")
                                }
                                renderInput={(params) => (
                                    <TextField {...params} />
                                )}
                            />
                            {!transfer.internalTransfer && (
                                <>
                                {transfer.isAirportTransfer ? (
                                    transfer.direction === "OUT" ? (
                                        <FormControl fullWidth>
                                            <InputLabel id="depart-airport-label">Departure airport (served)</InputLabel>
                                            <Select
                                                labelId="depart-airport-label"
                                                name="depart"
                                                value={transfer.depart || ""}
                                                onChange={handleChange}
                                                label="Departure airport (served)"
                                            >
                                                <MenuItem value=""><em>Select airport</em></MenuItem>
                                                    {airports.map((a) => (
                                                        <MenuItem key={a.code} value={a.code}>
                                                            {a.code} — {a.name}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <TextField
                                            label="Depart / origin info (optional)"
                                            name="depart"
                                            variant="standard"
                                            value={transfer.depart}
                                            onChange={handleChange}
                                        />
                                    )
                                ) : (
                                    transfer.direction === "OUT" ? (
                                        <TextField
                                            label="Drop-off point (free text)"
                                            name="depart"
                                            variant="standard"
                                            value={transfer.depart}
                                            onChange={handleChange}
                                            placeholder="e.g. Edinburgh city centre hotel"
                                        />
                                    ) : (
                                        <TextField
                                            label="Depart"
                                            name="depart"
                                            variant="standard"
                                            value=""
                                            disabled
                                            helperText="Not used for inbound non-airport transfers"
                                        />
                                    )
                                )}

                                {transfer.isAirportTransfer ? (
                                    transfer.direction === "IN" ? (
                                        <FormControl fullWidth>
                                            <InputLabel id="arrive-airport-label">Arrival airport (served)</InputLabel>
                                            <Select
                                                labelId="arrive-airport-label"
                                                name="arrive"
                                                value={transfer.arrive || ""}
                                                onChange={handleChange}
                                                label="Arrival airport (served)"
                                            >
                                                <MenuItem value=""><em>Select airport</em></MenuItem>
                                                    {airports.map((a) => (
                                                        <MenuItem key={a.code} value={a.code}>
                                                            {a.code} — {a.name}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <TextField
                                            label="Arrive / destination info (optional)"
                                            name="arrive"
                                            variant="standard"
                                            value={transfer.arrive}
                                            onChange={handleChange}
                                        />
                                    )
                                ) : (
                                    transfer.direction === "IN" ? (
                                        <TextField
                                            label="Pick-up point (free text)"
                                            name="arrive"
                                            variant="standard"
                                            value={transfer.arrive}
                                            onChange={handleChange}
                                            placeholder="e.g. Edinburgh city centre hotel"
                                        />
                                    ) : (
                                        <TextField
                                            label="Arrive"
                                            name="arrive"
                                            variant="standard"
                                            value=""
                                            disabled
                                            helperText="Not used for outbound non-airport transfers"
                                        />
                                    )
                                )}
                                </>
                            )}

                            {!transfer.internalTransfer && (
                                <>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="privatePickup"
                                                checked={transfer.privatePickup}
                                                onChange={handleChange}
                                            />
                                        }
                                        label="Private pickup / self transfer"
                                    />
                                    <TextField
                                        label="Flight Number"
                                        name="flightId"
                                        variant="standard"
                                        value={transfer.flightId}
                                        onChange={handleChange}
                                        disabled={transfer.privatePickup}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="unaccompaniedMinor"
                                                checked={transfer.unaccompaniedMinor}
                                                onChange={handleChange}
                                            />
                                        }
                                        label="Unaccompanied minor"
                                    />
                                </>
                            )}

                            <TimePicker
                                label="Departure Time"
                                value={transfer.departureTime}
                                onChange={(time) =>
                                    handleDateChange(time, "departureTime")
                                }
                                renderInput={(params) => (
                                    <TextField {...params} />
                                )}
                            />
                            <TimePicker
                                label="Arrival Time"
                                value={transfer.arrivalTime}
                                onChange={(time) =>
                                    handleDateChange(time, "arrivalTime")
                                }
                                renderInput={(params) => (
                                    <TextField {...params} />
                                )}
                            />

                            <TextField
                                label="Notes"
                                name="notes"
                                variant="standard"
                                value={transfer.notes}
                                onChange={handleChange}
                                multiline
                                minRows={2}
                            />
                        </Stack>
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSave}>Save</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default EditTransfer;
